/**
 * Layout Clone Templates Hooks (SoW-311)
 * React Query hooks for managing component layout presets
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { showToast } from '@/components/Notifications/Toast'
import type {
  LayoutCloneTemplate,
  CreateLayoutCloneTemplateDTO,
  UpdateLayoutCloneTemplateDTO,
} from '@fmeastudio/shared'
import {
  getLayoutTemplates,
  getLayoutTemplate,
  createLayoutTemplate,
  updateLayoutTemplate,
  deleteLayoutTemplate,
} from '@/api/layout-templates'
import { useProject } from '@/contexts/ProjectContext'

/**
 * Get all layout templates for the active project
 */
export function useLayoutTemplates() {
  const { activeProject } = useProject()

  return useQuery({
    queryKey: ['layoutTemplates', activeProject?.id],
    queryFn: () => {
      if (!activeProject) return []
      return getLayoutTemplates(activeProject.id)
    },
    enabled: !!activeProject,
  })
}

/**
 * Get a single layout template
 */
export function useLayoutTemplate(templateId: string) {
  const { activeProject } = useProject()

  return useQuery({
    queryKey: ['layoutTemplates', activeProject?.id, templateId],
    queryFn: () => {
      if (!activeProject) throw new Error('No active project')
      return getLayoutTemplate(activeProject.id, templateId)
    },
    enabled: !!activeProject && !!templateId,
  })
}

/**
 * Create a new layout template
 */
export function useCreateLayoutTemplate() {
  const queryClient = useQueryClient()
  const { activeProject } = useProject()

  return useMutation({
    mutationFn: (data: CreateLayoutCloneTemplateDTO) => {
      if (!activeProject) throw new Error('No active project')
      return createLayoutTemplate(activeProject.id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layoutTemplates'] })
      showToast.success('Layout template saved')
    },
    onError: (error: Error) => {
      // Check for duplicate name error
      if (error.message?.includes('already exists')) {
        showToast.error('A template with this name already exists')
      } else {
        showToast.error(error.message || 'Failed to save layout template')
      }
    },
  })
}

/**
 * Update an existing layout template
 */
export function useUpdateLayoutTemplate() {
  const queryClient = useQueryClient()
  const { activeProject } = useProject()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLayoutCloneTemplateDTO }) => {
      if (!activeProject) throw new Error('No active project')
      return updateLayoutTemplate(activeProject.id, id, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['layoutTemplates', activeProject?.id] })
      queryClient.invalidateQueries({
        queryKey: ['layoutTemplates', activeProject?.id, variables.id],
      })
      showToast.success('Layout template updated')
    },
    onError: (error: Error) => {
      if (error.message?.includes('already exists')) {
        showToast.error('A template with this name already exists')
      } else {
        showToast.error(error.message || 'Failed to update layout template')
      }
    },
  })
}

/**
 * Delete a layout template
 */
export function useDeleteLayoutTemplate() {
  const queryClient = useQueryClient()
  const { activeProject } = useProject()

  return useMutation({
    mutationFn: (templateId: string) => {
      if (!activeProject) throw new Error('No active project')
      return deleteLayoutTemplate(activeProject.id, templateId)
    },
    onSuccess: (_, templateId) => {
      queryClient.removeQueries({
        queryKey: ['layoutTemplates', activeProject?.id, templateId],
      })
      queryClient.invalidateQueries({ queryKey: ['layoutTemplates'] })
      showToast.success('Layout template deleted')
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to delete layout template')
    },
  })
}
